import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const REC_CATS = ["Photobooth", "Audio Guest Book", "Cabine", "Autre"];
const DEP_CATS = ["Consommables", "Accessoires photobooth", "Papier", "Rubans imprimante", "Matériel", "Frais divers", "Autre"];
const METHODS = [["paypal", "PayPal"], ["cb", "CB"], ["virement", "Virement"], ["especes", "Espèces"], ["autre", "Autre"]];
const EMPTY_REC = { date: new Date().toISOString().slice(0, 10), amount: "", description: "", category: "Autre", method: "virement", note: "", declarable: true, urssaf_free: false };
const EMPTY_DEP = { date: new Date().toISOString().slice(0, 10), amount: "", description: "", category: "Consommables", note: "" };

export default function FinanceEntries({ onChange }) {
  const [tab, setTab] = useState("recettes");
  const [recettes, setRecettes] = useState([]);
  const [depenses, setDepenses] = useState([]);
  const [rec, setRec] = useState(EMPTY_REC);
  const [dep, setDep] = useState(EMPTY_DEP);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/finance/recettes").then((r) => setRecettes(r.data)).catch(() => {});
    api.get("/finance/depenses").then((r) => setDepenses(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const isRec = tab === "recettes";
    const form = isRec ? rec : dep;
    if (!(parseFloat(form.amount) > 0) || !form.date) { toast.error("Montant et date requis"); return; }
    if (isRec && !form.description.trim()) { toast.error("Description requise"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editId) {
        await api.put(`/finance/${tab}/${editId}`, payload);
        toast.success("Écriture modifiée");
      } else {
        await api.post(`/finance/${tab}`, payload);
        toast.success(isRec ? "Recette ajoutée" : "Dépense ajoutée");
      }
      setRec(EMPTY_REC);
      setDep(EMPTY_DEP);
      setEditId(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette écriture financière ?")) return;
    await api.delete(`/finance/${tab}/${id}`);
    toast.success("Écriture supprimée");
    load();
    onChange?.();
  };

  const items = tab === "recettes" ? recettes : depenses;
  const form = tab === "recettes" ? rec : dep;
  const setForm = tab === "recettes" ? setRec : setDep;
  const cats = tab === "recettes" ? REC_CATS : DEP_CATS;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="finance-entries">
      <h2 className="font-heading text-base md:text-lg font-bold">Recettes & dépenses hors réservation</h2>
      <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1 gap-1 w-fit" data-testid="finance-tabs">
        {[["recettes", "Recettes"], ["depenses", "Dépenses"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`finance-tab-${v}`} onClick={() => { setTab(v); setEditId(null); }}
            className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${tab === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            {l} ({v === "recettes" ? recettes.length : depenses.length})
          </button>
        ))}
      </div>

      {items.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="finance-list">
          {items.map((it, i) => (
            <li key={it.id} data-testid={`finance-line-${i}`} className="py-2 flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1">
                <span className="font-mono tabular-nums font-semibold">{fmtEUR(it.amount)}</span>
                <span className="text-slate-500"> — le {fmtDate(it.date)}</span>
                {it.description ? <span className="font-medium"> · {it.description}</span> : null}
                <span className="text-slate-400"> · {it.category}{it.method ? ` · ${(METHODS.find(([v]) => v === it.method) || [])[1] || it.method}` : ""}</span>
                {tab === "recettes" && !it.declarable && <span className="ml-1 rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[9px] font-bold uppercase">hors CA</span>}
                {tab === "recettes" && it.urssaf_free && <span className="ml-1 rounded bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[9px] font-bold uppercase">hors URSSAF</span>}
              </span>
              <Button variant="ghost" size="icon" data-testid={`finance-edit-${i}`} className="h-7 w-7 text-slate-500 shrink-0"
                onClick={() => { setEditId(it.id); setForm({ ...(tab === "recettes" ? EMPTY_REC : EMPTY_DEP), ...it }); }}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" data-testid={`finance-delete-${i}`} className="h-7 w-7 text-rose-600 hover:bg-rose-50 shrink-0" onClick={() => del(it.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3.5 space-y-2.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {editId ? "Modifier l'écriture" : tab === "recettes" ? "Ajouter une recette" : "Ajouter une dépense"}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Input data-testid="finance-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input data-testid="finance-amount" type="number" min="0" step="0.01" placeholder="Montant €" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <select data-testid="finance-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 rounded-md border border-input bg-white px-2 text-sm">
            {cats.map((cname) => <option key={cname} value={cname}>{cname}</option>)}
          </select>
          {tab === "recettes" ? (
            <select data-testid="finance-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="h-10 rounded-md border border-input bg-white px-2 text-sm">
              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ) : (
            <Input data-testid="finance-note" placeholder="Remarque (facultatif)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          )}
        </div>
        <Input data-testid="finance-description" placeholder={tab === "recettes" ? "Description (ex. prestation mairie)" : "Description (facultatif)"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        {tab === "recettes" && (
          <div className="flex flex-col gap-1.5">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="finance-declarable" checked={form.declarable} onChange={(e) => setForm({ ...form, declarable: e.target.checked })} className="h-4 w-4 accent-sky-600" />
              Comptabiliser directement comme chiffre d'affaires
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="finance-urssaf-free" checked={form.urssaf_free} onChange={(e) => setForm({ ...form, urssaf_free: e.target.checked })} className="h-4 w-4 accent-violet-600" />
              Ne pas appliquer la déduction URSSAF de 22,9 %
            </label>
          </div>
        )}
        <div className="flex gap-2">
          <Button data-testid="finance-save" onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editId ? "Enregistrer la modification" : "Ajouter"}
          </Button>
          {editId && <Button variant="ghost" data-testid="finance-cancel-edit" onClick={() => { setEditId(null); setRec(EMPTY_REC); setDep(EMPTY_DEP); }}><X className="h-4 w-4" /> Annuler</Button>}
        </div>
      </div>
    </section>
  );
}
